import { useEffect, useState, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import { useCatalog } from '../hooks/useCatalog';
import { getBestThumbnailUrl } from '../hooks/useObjectImages';
import { getCrossOrigin } from '../utils/imageUtils';
import { logger } from '../utils/logger';

// Module-level cache keyed by `${catalogItemId}:${ownerId ?? 'self'}`.
// Presigned URLs expire, so we never persist these to the backend — the
// cache lives only for the current page load and is re-seeded on reload.
const thumbnailCache = new Map<string, string | null>();
const inflight = new Map<string, Promise<void>>();

interface RelatedWorkThumbnailProps {
  catalogItemId: string;
  title: string;
  ownerId?: string | null;
}

export function RelatedWorkThumbnail({
  catalogItemId,
  title,
  ownerId,
}: RelatedWorkThumbnailProps) {
  const { getCatalogItem, getRelatedItemThumbnail } = useCatalog();
  // Cache key includes ownerId so a delegator's item and a self-owned item
  // with the same catalogItemId never collide (different S3 access scopes).
  const cacheKey = `${catalogItemId}:${ownerId ?? 'self'}`;
  const cached = thumbnailCache.get(cacheKey);
  const [url, setUrl] = useState<string | null | undefined>(cached);
  const [loading, setLoading] = useState<boolean>(!thumbnailCache.has(cacheKey));
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (thumbnailCache.has(cacheKey)) {
      // Sync state to the (possibly new) cacheKey. useState's lazy initial
      // value ran only on mount, so if catalogItemId or ownerId change on an
      // existing instance and the new key is already cached, we must update
      // state explicitly here.
      setUrl(thumbnailCache.get(cacheKey));
      setLoading(false);
      return;
    }

    setLoading(true);
    const existing = inflight.get(cacheKey);
    const promise =
      existing ??
      (async () => {
        try {
          // GH#1177: When ownerId is provided, the caller is in delegate mode
          // and must use the owner-scoped endpoint so delegation auth is enforced.
          // When ownerId is absent, use the public-aware endpoint so that public
          // related items render their thumbnails for non-owner viewers.
          const item = ownerId
            ? await getCatalogItem(catalogItemId, ownerId)
            : await getRelatedItemThumbnail(catalogItemId);
          const images = item.images ?? [];
          const coverImage =
            images.find((i) => i.imageId === item.coverImageId) ?? images[0];
          const resolved = coverImage
            ? getBestThumbnailUrl(coverImage) ?? null
            : null;
          thumbnailCache.set(cacheKey, resolved);
        } catch (err) {
          logger.warn('Failed to load related-work thumbnail', {
            catalogItemId,
            err,
          });
          thumbnailCache.set(cacheKey, null);
        } finally {
          inflight.delete(cacheKey);
        }
      })();

    if (!existing) inflight.set(cacheKey, promise);

    promise.then(() => {
      if (!mountedRef.current) return;
      setUrl(thumbnailCache.get(cacheKey));
      setLoading(false);
    });
  }, [cacheKey, catalogItemId, ownerId, getCatalogItem, getRelatedItemThumbnail]);

  return (
    <Box
      data-testid="related-work-thumbnail"
      sx={{
        width: 48,
        height: 48,
        borderRadius: 1,
        overflow: 'hidden',
        flexShrink: 0,
        bgcolor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {loading && (
        <CircularProgress size={18} aria-label="Loading thumbnail" />
      )}
      {!loading && url && (
        <img
          src={url}
          alt={title}
          crossOrigin={getCrossOrigin(url)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      {!loading && !url && (
        <ImageIcon
          fontSize="small"
          sx={{ color: 'grey.400' }}
          aria-hidden
        />
      )}
    </Box>
  );
}
